import React, { useEffect, useRef, useState } from "react"
import { Modal, ModalBody } from "reactstrap"
import "../../../../src/assets/scss/custom/chatbotUI.scss"
import "../../../pages/ChatBot/ChatBot.css"
import { marked } from "marked"
import DOMPurify from "dompurify"
import axios from "axios"
import {ai_api} from "../../../utils/variables"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

const ChatbotUI = ({ toggle, togglemodal, initialPrompt, pageDescription, aiReport }) => {
  const { trackEvent } = useAnalytics()
  const [messages, setMessages] = useState([])
  const initialPromptSentRef = useRef(false)
  const pageDescriptionShownRef = useRef(false)
  const aiReportContextSentRef = useRef(false)
  const isOpenRef = useRef(toggle)
  const abortControllerRef = useRef(null)
  const requestIdRef = useRef(0)
  const conversationIdRef = useRef(null)
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  // When modal opens, show page description first (if provided) or a greeting,
  // then optionally send the initialPrompt programmatically (once per open).
  useEffect(() => {
    // keep a live ref of open state for async callbacks
    isOpenRef.current = toggle

    if (!toggle) {
      // reset flags when modal is closed so each open behaves the same
      initialPromptSentRef.current = false
      pageDescriptionShownRef.current = false
      aiReportContextSentRef.current = false
      // reset conversation_id so each modal open starts a fresh conversation
      conversationIdRef.current = null
      // remove page blur class when modal closed
      try { document.body.classList.remove('chatbot-open') } catch (e) { }
      // abort any in-flight request
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
          abortControllerRef.current = null
        }
      } catch (e) {
        // ignore
      }

      // increment request id to invalidate pending responses
      requestIdRef.current += 1
      return
    }

    // Start with a fresh message list for each open so the description appears first
    setMessages([])

    // add a class on the body so we can blur the page behind the modal with CSS
    try { document.body.classList.add('chatbot-open') } catch (e) { }

    const showDescriptionAndMaybeSend = async () => {
      // Show page description (or default greeting)
      if (pageDescription && !pageDescriptionShownRef.current) {
        pageDescriptionShownRef.current = true
        setMessages(prev => [...prev, { from: "bot", text: pageDescription }])
      } else if (!pageDescription && !pageDescriptionShownRef.current) {
        // No page description and user requested no greeting — don't show the default greeting.
        // Still mark description-as-shown so initialPrompt can proceed and we don't re-run this block.
        pageDescriptionShownRef.current = true
      }

      // Show AI report as context if provided
      if (aiReport && !pageDescriptionShownRef.current) {
        pageDescriptionShownRef.current = true
        setMessages(prev => [...prev, { 
          from: "bot", 
          text: `**Current AI Report Context:**\n\n${aiReport}` 
        }])
      }

      // If there's an initialPrompt and it hasn't been sent yet, send it after a short delay
      if (initialPrompt && !initialPromptSentRef.current) {
        initialPromptSentRef.current = true
        // small delay so user sees the context first
        setTimeout(async () => {
          // assign a request id so late responses can be ignored
          const reqId = ++requestIdRef.current
          try {
            // Add the user's prompt to the chat so they can see what they asked
            setMessages(prev => [...prev, { from: "You", text: initialPrompt }])
            setLoading(true)
            const response = await fetchBotResponse(initialPrompt)
            // if modal closed or another request started, ignore this response
            if (!isOpenRef.current || requestIdRef.current !== reqId) return
            const botMessage = { from: "bot", text: response }
            setMessages(prev => [...prev, botMessage])
          } catch (err) {
            // ignore aborted/canceled requests
            const aborted = err?.name === "AbortError" || err?.code === "ERR_CANCELED" || /cancel/i.test(err?.message || "")
            if (aborted) return
            setMessages(prev => [...prev, { from: "bot", text: "Something went wrong." }])
          } finally {
            if (isOpenRef.current) setLoading(false)
          }
        }, 700)
      }
    }

    showDescriptionAndMaybeSend()
  }, [toggle, initialPrompt, pageDescription])

  const formatText = text => {
    if (!text) return null
    const ftext = text
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/# (.*?)\n/g, '<h5 style="margin-top: 16px">$1</h5>')
      .replace(/- \*\*(.*?)\*\*:/g, "<li><b>$1:</b>")
      .replace(/- (.*?)\n/g, "<li>$1</li>")
      .replace(/\n/g, "<br/>")
    return <span dangerouslySetInnerHTML={{ __html: ftext }} />
  }

  function isMarkdownTable(text) {
    return /\|.+\|/.test(text) && /[-]+/.test(text)
  }

  function convertMarkdownToHtmlTable(md) {
    const rawHtml = marked(md)
    return DOMPurify.sanitize(rawHtml)
  }

  async function fetchBotResponse(inputValue) {
    // create an abort controller per request and attach to ref so it can be aborted on close
    try {
      if (abortControllerRef.current) {
        // if an earlier controller exists, leave it — we create a fresh one for this request
        abortControllerRef.current = null
      }
    } catch (e) {
      // ignore
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    
    // Prepare the query with AI report context if available (only for first query)
    let query = inputValue
    if (aiReport && !aiReportContextSentRef.current) {
      query = `**Context from Current AI Report:**\n\n${aiReport}\n\n**Answer below query based on above data:**\n\n${inputValue}`
      aiReportContextSentRef.current = true
    }
    
    const gamedata = { query: query }
    // For follow-up questions in the same session, include the conversation_id
    if (conversationIdRef.current) {
      gamedata.conversation_id = conversationIdRef.current
    }

    const res = await axios.post(`${ai_api}/chat/`, gamedata, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      signal: controller.signal,
    })
    // Store conversation_id so subsequent calls stay in the same conversation
    if (res.data?.conversation_id) {
      conversationIdRef.current = res.data.conversation_id
    }
    return res.data?.response
  }

  const handleSend = async () => {
    if (!input.trim()) return
    const userText = input
    const updated = [...messages, { from: "You", text: userText }]
    setMessages(updated)
    setInput("")
    // track request id so late responses can be ignored
    const reqId = ++requestIdRef.current
    setLoading(true)
    try {
      const response = await fetchBotResponse(userText)
      if (!isOpenRef.current || requestIdRef.current !== reqId) return
      const botMessage = { from: "bot", text: response }
      setMessages(prev => [...prev, botMessage])

      trackEvent(ANALYTICS_EVENTS.FEATURE.ASSISTANT_CHAT_MESSAGE_SENT, { surface: "ask_ai_widget" })
    } catch (err) {
      const aborted = err?.name === "AbortError" || err?.code === "ERR_CANCELED" || /cancel/i.test(err?.message || "")
      if (aborted) return
      setMessages(prev => [
        ...prev,
        { from: "bot", text: "Something went wrong." },
      ])
    } finally {
      if (isOpenRef.current) setLoading(false)
    }
  }

  return (
    <>
      <Modal
        isOpen={toggle}
        toggle={() => togglemodal()}
        centered
        size="lg"
        modalClassName="chatbot-modal"
        style={{ maxHeight: "85vh" }}
      >
        <div className="modal-header">
          <div className="d-flex justify-content-start gap-2 align-items-center">
            <h5 className="modal-title">Keyquest Assistance</h5>
          </div>
          <button type="button" className="btn-close" onClick={() => togglemodal()} aria-label="Close"></button>
        </div>

        <ModalBody className="chatbot-body bg-transparent" style={{ maxHeight: "80vh", minHeight: "40vh", padding: 0 }}>
          <div className="chatbot-container">
            <div className="chat-messages-container">
              <div className="chat-messages">

                {messages && messages.map((message, idx) => (
                  <React.Fragment key={"message_" + idx}>
                    <div className={`message-item ${message.from === "You" ? "user-message" : "ai-message"}`}>
                      {message.from !== "You" && (
                        <div className="message-sender-label">AI Assistant</div>
                      )}

                      <div className="message-row">
                        <div className="message-avatar">
                          {message.from === "You" ? (
                            <div className="user-avatar">
                              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face&rounded=full" alt="User" />
                            </div>
                          ) : (
                            <div className="ai-avatar">
                              <img src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=32&h=32&fit=crop&crop=face&rounded=full" alt="AI Assistant" />
                            </div>
                          )}
                        </div>

                        <div className="message-content">
                          <div className="message-bubble">
                            {isMarkdownTable(message.text) ? (
                              <div className="markdown-table" dangerouslySetInnerHTML={{ __html: convertMarkdownToHtmlTable(message.text) }} />
                            ) : (
                              <div className="message-text">{formatText(message.text)}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {message.from === "You" && (
                        <div className="message-sender-label user-label">User</div>
                      )}
                    </div>
                    {idx === 0 && (<div className="day-divider">Today</div>)}
                  </React.Fragment>
                ))}

                {loading && (
                  <div className="message-item ai-message">
                    <div className="message-sender-label">AI Assistant</div>
                    <div className="message-row">
                      <div className="message-avatar">
                        <div className="ai-avatar">
                          <img src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=32&h=32&fit=crop&crop=face&rounded=full" alt="AI Assistant" />
                        </div>
                      </div>

                      <div className="message-content">
                        <div className="message-bubble loading-bubble">
                          <div className="loading-dots">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef}></div>
              </div>
            </div>

            <div className="chat-input-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  className="chat-input"
                  value={input}
                  onKeyDown={e => e.key === "Enter" && handleSend()}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Enter Message..."
                />
                <button className="send-button" onClick={() => handleSend()} disabled={loading || !input.trim()}>
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>
                  <span>Send</span>
                </button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default ChatbotUI

